import { ColDef, GridOptions } from 'ag-grid-enterprise';

import {
  buildColDef,
  ColType,
  columnSize,
} from 'app/shared/utils/ag-grid.col-def.utils';
import { DecimalPlacesUtils } from 'v2/modules/shared/util/decimal-places.utils';

import { CostLayerQuantityProgressCellComponent } from '../components/cost-layer-quantity-progress-cell/cost-layer-quantity-progress-cell.component';
import { ECostLayerField } from '../cost-layers.const';

//

export const COST_LAYERS_AG_GRID_OPTIONS :GridOptions = {
  cacheBlockSize: 50,
  defaultColDef: {
    floatingFilter: true,
    sortable: true,
    resizable: true,
    width: 0,
  },
  pagination: true,
  paginationPageSize: 50,
  rowModelType: 'serverSide',
  sideBar: {
    defaultToolPanel: '',
    toolPanels: [
      {
        iconKey: 'columns',
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressPivotMode: true,
          suppressPivots: true,
          suppressRowGroups: true,
          suppressValues: true,
        },
      },
      {
        iconKey: 'filter',
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        toolPanel: 'agFiltersToolPanel',
      },
    ],
  },
  suppressAutoSize: false,
};

export const CostLayersAgGridColumnDef = {
  COST_TOTAL: buildColDef(ColType.CURRENCY, {
    field: ECostLayerField.COST_TOTAL,
    headerName: 'Total Cost',
    ...columnSize(15),
  }),
  COST_UNIT: buildColDef(ColType.CURRENCY, {
    field: ECostLayerField.COST_UNIT,
    headerName: 'Unit Cost',
    ...columnSize(14),
    valueFormatter: ({ value }) => DecimalPlacesUtils.format(value),
  }),
  DATE_CREATED: buildColDef(
    ColType.DATETIME,
    {
      field: ECostLayerField.DATE_CREATED,
      headerName: 'Created',
      sort: 'desc', // sort by created date (newer to older) by default
      ...columnSize(16),
    },
    {
      routerLinkFormatter: ({ data }) => `/cost-layers/${data.id}`,
    },
  ),
  DATE_UPDATED: buildColDef(ColType.DATETIME, {
    field: ECostLayerField.DATE_UPDATED,
    headerName: 'Updated',
    ...columnSize(16),
  }),
  LOT_NUMBER: buildColDef(ColType.DEFAULT, {
    field: ECostLayerField.LOT_NUMBER,
    headerName: 'Lot Number',
    ...columnSize(18),
  }),
  SKU: buildColDef(
    ColType.DEFAULT,
    {
      field: ECostLayerField.PRODUCT_SKU,
      headerName: 'SKU',
      ...columnSize(18),
      valueGetter: ({ data }) => data.product?.internalSku ?? '(DELETED SKU)',
    },
    {
      routerLinkFormatter: ({ data }) =>
        `/inventory/${data.productId}/view?v=costs`,
    },
  ),
  QUANTITY: buildColDef(ColType.DEFAULT, {
    field: 'quantityGraphic', // temporary, 'til there are backend filters to use
    headerName: 'Quantity',
    ...columnSize(18),
    cellRenderer: CostLayerQuantityProgressCellComponent,
    filter: false,
    floatingFilter: false,
    sortable: false,
  }),
  QUANTITY_REMAINING: buildColDef(ColType.QUANTITY, {
    field: ECostLayerField.QUANTITY_REMAINING,
    headerName: 'Remaining Qty',
    ...columnSize(14),
    hide: true,
  }),
  QUANTITY_INITIAL: buildColDef(ColType.QUANTITY, {
    field: ECostLayerField.QUANTITY_INITIAL,
    headerName: 'Initial Qty',
    ...columnSize(12),
    hide: true,
  }),
  VARIANCE: buildColDef(ColType.BOOLEAN, {
    field: ECostLayerField.IS_VARIANCE,
    headerName: 'Variance',
    ...columnSize(11),
    filterParams: {
      filterOptions: [
        {
          displayKey: 'empty',
          displayName: 'All Layers',
          predicate: () => true,
          numberOfInputs: 0,
        },
        {
          displayKey: 'non-variance',
          displayName: 'Standard Layers',
          predicate: (value) => value,
          numberOfInputs: 0,
        },
        {
          displayKey: 'variance',
          displayName: 'Variance Layers',
          predicate: (value) => !value,
          numberOfInputs: 0,
        },
      ],
      defaultOption: 'empty',
      maxNumConditions: 1,
      debounceMs: 1000,
    },
    valueFormatter: ({ value }) => (value ? 'variance' : ''),
  }),
  WAREHOUSE: buildColDef(ColType.DEFAULT, {
    field: ECostLayerField.WAREHOUSE_NAME,
    headerName: 'Warehouse',
    ...columnSize(20),
  }),
  COST_QUANTITY_REMAINING: buildColDef(ColType.CURRENCY, {
    field: ECostLayerField.COST_QUANTITY_REMAINING,
    headerName: 'Remaining Qty Cost',
    ...columnSize(15),
  }),
} as const;

export const COST_LAYERS_AG_GRID_COLUMN_DEFS :ColDef[] = [
  CostLayersAgGridColumnDef.DATE_CREATED,
  CostLayersAgGridColumnDef.WAREHOUSE,
  CostLayersAgGridColumnDef.SKU,
  CostLayersAgGridColumnDef.COST_UNIT,
  CostLayersAgGridColumnDef.COST_TOTAL,
  CostLayersAgGridColumnDef.LOT_NUMBER,
  CostLayersAgGridColumnDef.QUANTITY_REMAINING,
  CostLayersAgGridColumnDef.QUANTITY_INITIAL,
  CostLayersAgGridColumnDef.QUANTITY,
  CostLayersAgGridColumnDef.DATE_UPDATED,
  CostLayersAgGridColumnDef.VARIANCE,
  CostLayersAgGridColumnDef.COST_QUANTITY_REMAINING,
];
